import { FC, useCallback, useMemo, useState } from "react";
import { DropsTableGrid } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/ActionableTrains/DropsTableGrid";
import {
  getActionableTrainAssistanceColumns,
  getActionableTrainTrainColumns,
  ActionableTrainSectionWrapper,
  actionableTrainTrainTableItemRenderer,
  assistanceEventTableItemRenderer,
} from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/ActionableTrains/actionableTrainSectionHelpers";
import { ActionableTrainSectionHeader } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/ActionableTrains/ActionableTrainSectionHeader";
import {
  AltTransIncidentArrivalStatus,
  AltTransIncidentTrain,
} from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/types";
import { Text } from "shared/components/Spor";
import {
  aggregateTrainCount,
  isSameTrainId,
} from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/ActionableTrains/utils";
import {
  ActionableTrainSectionFooter,
  TRAIN_LIMIT,
} from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/ActionableTrains/ActionableTrainSectionFooter";
import { AssistanceEvent } from "features/CenterContent/RoleContent/AlternativeTransport/DecisionSupport/components/ActionableTrains/AssistanceEventGridItem/AssistanceType";

type ActionableTrainSectionProps = {
  affectedTrains?: AltTransIncidentTrain[] | null;
  assistanceEvents?: AssistanceEvent[] | null;
  title: string;
  isCentreSection?: boolean;
};

export const ActionableTrainSection: FC<ActionableTrainSectionProps> = ({
  affectedTrains,
  assistanceEvents,
  title,
  isCentreSection = false,
}) => {
  const [sectionCollapsed, _setSectionCollapsed] = useState(false);
  const toggleSectionCollapsed = useCallback(
    () => _setSectionCollapsed((b) => !b),
    [],
  );

  const [showDeparted, setShowDeparted] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const toggleDeparted = useCallback(() => setShowDeparted((d) => !d), []);
  const toggleAll = useCallback(() => setShowAll((a) => !a), []);

  const trainsToShow = useMemo(
    () =>
      affectedTrains
        // First remove departed trains if applicable
        ?.filter(
          (train) =>
            showDeparted ||
            train.incidentArrival.status !==
              AltTransIncidentArrivalStatus.DEPARTED_INCIDENT,
        )
        // Then limit the length if applicable
        .slice(0, showAll ? undefined : TRAIN_LIMIT) ?? [],
    [showAll, showDeparted, affectedTrains],
  );

  const eventsToShow = useMemo(
    () =>
      assistanceEvents?.filter((event) =>
        trainsToShow.some((train) =>
          isSameTrainId(event.trainIdentifier, train.id),
        ),
      ) ?? [],
    [assistanceEvents, trainsToShow],
  );

  const collapseAwareTrainItemRenderer = useCallback(
    (item: AltTransIncidentTrain) =>
      actionableTrainTrainTableItemRenderer(item, sectionCollapsed),
    [sectionCollapsed],
  );

  const collapseAwareAssistanceEventItemRenderer = useCallback(
    (item: AssistanceEvent) =>
      assistanceEventTableItemRenderer(item, sectionCollapsed),
    [sectionCollapsed],
  );

  const currentTotalPersonCount = useMemo(
    () => (affectedTrains ? aggregateTrainCount(affectedTrains) : 0),
    [affectedTrains],
  );

  return (
    <ActionableTrainSectionWrapper $collapsed={sectionCollapsed}>
      <ActionableTrainSectionHeader
        sectionCollapsed={sectionCollapsed}
        toggleSectionCollapsed={toggleSectionCollapsed}
        isCentreSection={isCentreSection}
        title={title}
        affectedTrainsCount={affectedTrains?.length}
        showDeparted={showDeparted}
        toggleDeparted={toggleDeparted}
      />

      <Text
        fontWeight="bold"
        px={2}
        py={1}
        border="1px solid"
        borderColor="semantics.colorOutline"
        borderRadius="sm"
        mt="-2" // To counteract gap=4 on parent
      >
        {sectionCollapsed
          ? `Berørte: ${currentTotalPersonCount}`
          : `Berørte reisende: ${currentTotalPersonCount}`}
      </Text>

      {/* Table: assistansebehov */}
      <DropsTableGrid
        columns={getActionableTrainAssistanceColumns(sectionCollapsed)}
        items={eventsToShow}
        itemRenderer={collapseAwareAssistanceEventItemRenderer}
        emptyText={sectionCollapsed ? "Ingen" : "Ingen assistansebehov"}
      />

      {/* Table: tog */}
      <DropsTableGrid
        columns={getActionableTrainTrainColumns(
          sectionCollapsed,
          isCentreSection,
        )}
        items={trainsToShow}
        itemRenderer={collapseAwareTrainItemRenderer}
        emptyText={
          sectionCollapsed ? "Ingen" : "Ingen tog på strekningen i perioden"
        }
      />

      <ActionableTrainSectionFooter
        toggleAll={toggleAll}
        showAll={showAll}
        currentLength={trainsToShow.length}
        totalLength={affectedTrains?.length ?? 0}
      />
    </ActionableTrainSectionWrapper>
  );
};
